import type { QueryClient } from '@tanstack/react-query';
import {
  FunnelInteractionType,
  type Call_V2,
  type FunnelConversationDocument,
} from 'src/api/generated';
import { getRecentCall } from '../timeline-items/recent-events.util';

/**
 * Formats the same Call summary for realtime updates and HTTP reconciliation.
 * @param call - Canonical Call summary fields.
 * @returns Category, duration or the ordinary Call label.
 */
export const getCallSnippet = (
  call: Pick<Call_V2, 'category' | 'duration'>
): string => {
  const category = call.category?.trim();
  if (category) return category;
  return call.duration ? `Llamada ${call.duration}s` : 'Llamada';
};

/**
 * Preserves a newer accepted Call preview while a board HTTP read lags indexing.
 * Membership, counts, ordering and unrelated fields stay owned by search. Equal
 * or newer HTTP revisions settle ambiguity and the existing overlay TTL bounds retention.
 * @param queryClient - Account-owned cache containing accepted Call events.
 * @param document - Search-owned row, including its existing preview identity.
 * @returns Row with only the same interaction's stale summary corrected.
 */
export const reconcileCallPreview = (
  queryClient: QueryClient,
  document: FunnelConversationDocument
): FunnelConversationDocument => {
  if (document.lastInteraction?.modelType !== FunnelInteractionType.CALL) {
    return document;
  }
  const call = getRecentCall(
    queryClient,
    document.tenantId,
    document.lastInteraction.id
  );
  if (
    !call ||
    call.funnelConversationId !== document.id ||
    !(Date.parse(call.updatedAt) > Date.parse(document.updatedAt))
  ) {
    return document;
  }
  return {
    ...document,
    lastInteraction: {
      ...document.lastInteraction,
      status: call.status,
      snippet: getCallSnippet(call),
    },
  };
};
